// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Line comments rather than a /* */ block: the original text contains
// comment terminators, which would close a block comment early.
//
// The active implementation follows after this block. Nothing above it is live
// code — it is here so the previous behaviour stays readable in place.
// =============================================================================

// import { Locale } from "./locale";
//
// /** The mockups render "Jun 20, 2026" in English and "20 juin 2026" in French. */
// const OPTIONS: Record<Locale, Intl.DateTimeFormatOptions> = {
//   en: { month: "short", day: "numeric", year: "numeric" },
//   fr: { day: "numeric", month: "long", year: "numeric" },
// };
//
// const TAGS: Record<Locale, string> = { en: "en-US", fr: "fr-FR" };
//
// /**
//  * Formats a plain calendar date (`YYYY-MM-DD`) for display.
//  *
//  * Forced to UTC: a check-in is a calendar day, not an instant, and parsing a
//  * bare ISO date yields midnight UTC. Formatting that in a timezone behind
//  * Greenwich would roll it back to the previous day.
//  */
// export const formatDate = (isoDate: string, locale: Locale) =>
//   new Intl.DateTimeFormat(TAGS[locale], {
//     ...OPTIONS[locale],
//     timeZone: "UTC",
//   }).format(new Date(`${isoDate}T00:00:00Z`));

// ==== ACTIVE IMPLEMENTATION ==================================================

import { Locale } from "./locale";

/** The mockups render "Jun 20, 2026" in English and "20 juin 2026" in French. */
const OPTIONS: Record<Locale, Intl.DateTimeFormatOptions> = {
  en: { month: "short", day: "numeric", year: "numeric" },
  fr: { day: "numeric", month: "long", year: "numeric" },
};

/**
 * The desktop stay summary sets its dates in a two-column pair, where a
 * zero-padded day keeps "03 Sep" and "03 Dec" the same width: "03 Sep 2026" /
 * "03 déc. 2026". en-GB rather than en-US for the day-first order.
 */
const STAY_OPTIONS: Record<Locale, Intl.DateTimeFormatOptions> = {
  en: { day: "2-digit", month: "short", year: "numeric" },
  fr: { day: "2-digit", month: "short", year: "numeric" },
};

const TAGS: Record<Locale, string> = { en: "en-US", fr: "fr-FR" };

const STAY_TAGS: Record<Locale, string> = { en: "en-GB", fr: "fr-FR" };

/**
 * Formats a plain calendar date (`YYYY-MM-DD`) for display.
 *
 * Forced to UTC: a check-in is a calendar day, not an instant, and parsing a
 * bare ISO date yields midnight UTC. Formatting that in a timezone behind
 * Greenwich would roll it back to the previous day.
 */
export const formatDate = (isoDate: string, locale: Locale) =>
  new Intl.DateTimeFormat(TAGS[locale], {
    ...OPTIONS[locale],
    timeZone: "UTC",
  }).format(new Date(`${isoDate}T00:00:00Z`));

/**
 * The zone the properties are in. Paris and Brussels are both Europe/Paris —
 * same offset, same DST changeovers — so one zone covers every stay.
 */
export const PROPERTY_TIME_ZONE = "Europe/Paris";

/**
 * Today where the property is, as `YYYY-MM-DD`.
 *
 * en-CA because it formats as ISO. Deriving the date through Intl rather than
 * by arithmetic keeps it right across both DST changeovers, and independent of
 * the tenant's own device clock zone — a tenant reading this from New York must
 * not be told their stay has not started when it is already tomorrow in Paris.
 */
export const propertyToday = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: PROPERTY_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);

/**
 * Whether the stay has begun, in the property's own time.
 *
 * Compared as calendar dates: check-in is a day, not an instant, and both sides
 * are `YYYY-MM-DD`, where a string comparison is a date comparison. The step
 * opens at midnight in Paris on the arrival day — the DMS records no arrival
 * time this screen could read, and its `check_in_time` is free text
 * ("From 1:30 PM"), too unreliable to gate a signature on.
 */
export const hasCheckInPassed = (
  checkInIso: string,
  now: Date = new Date()
): boolean => {
  if (!checkInIso) return false;
  return propertyToday(now) >= checkInIso.slice(0, 10);
};

/** As `formatDate`, in the day-first form the stay summary uses. */
export const formatStayDate = (isoDate: string, locale: Locale) =>
  new Intl.DateTimeFormat(STAY_TAGS[locale], {
    ...STAY_OPTIONS[locale],
    timeZone: "UTC",
  }).format(new Date(`${isoDate}T00:00:00Z`));

/**
 * Today, spelled out: "02 December 2026". Used where a signature is dated — it
 * is the day the tenant signs, not a date drawn from the reservation, so it is
 * deliberately read from the clock rather than passed in.
 */
export const formatSignatureDate = (locale: Locale) =>
  new Intl.DateTimeFormat(STAY_TAGS[locale], {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date());

/** Day and month only — the opening half of a range that shares a year. */
const formatStayDayMonth = (isoDate: string, locale: Locale) =>
  new Intl.DateTimeFormat(STAY_TAGS[locale], {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${isoDate}T00:00:00Z`));

const yearOf = (isoDate: string) => isoDate.slice(0, 4);

/**
 * A rental period as one line: "03 Sep — 03 Dec 2026". The year is printed once
 * when both ends share it, and on both ends when the stay crosses New Year.
 *
 * Built by hand rather than with `Intl.DateTimeFormat.formatRange`, which this
 * project's TypeScript lib does not declare.
 */
export const formatStayRange = (
  startIso: string,
  endIso: string,
  locale: Locale
) => {
  const end = formatStayDate(endIso, locale);
  const start =
    yearOf(startIso) === yearOf(endIso)
      ? formatStayDayMonth(startIso, locale)
      : formatStayDate(startIso, locale);

  return `${start} — ${end}`;
};
